import { useState, useEffect } from "react";
import BarcodeScanner from "react-qr-barcode-scanner";
import { Result } from "@zxing/library";
import { useMutation } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";
import { useTransactionStore } from "@/features/Cart/store/transactionStore";
import { useShallow } from "zustand/shallow";
import type { CatalogItem } from "@/types/catalog.type";
import toast from "react-hot-toast";
import { getStoredUserData } from "@/utils/getStoredUser";
import { Smartphone } from "lucide-react";

export default function BarcodeScanBase() {
  const [lastBarcode, setLastBarcode] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(true);
  const userData = getStoredUserData();
  const email = userData?.email || "";

  const [addToCart] = useTransactionStore(
    useShallow((state) => [state.addToCart]),
  );

  // Deteksi mobile device dan orientasi
  useEffect(() => {
    const checkMobile = () => {
      const ua = navigator.userAgent.toLowerCase();
      const isAndroid = /android/i.test(ua);
      const isIOS = /iphone|ipad|ipod/i.test(ua);
      return isAndroid || isIOS;
    };

    const checkOrientation = () => {
      // Cek apakah landscape (width > height)
      const landscape = window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);
    };

    const mobile = checkMobile();
    setIsMobile(mobile);
    checkOrientation();

    // Listen untuk perubahan orientasi
    const handleOrientationChange = () => {
      checkOrientation();
    };

    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);

    if (mobile) {
      // Lock orientation to landscape
      if (screen.orientation && screen.orientation.lock) {
        screen.orientation.lock("landscape").catch((err) => {
          console.log("Orientation lock failed:", err);
        });
      }
    }

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);

      if (mobile && screen.orientation && screen.orientation.unlock) {
        screen.orientation.unlock();
      }
    };
  }, []);

  const handleAddToCart = (product: CatalogItem) => {
    addToCart(
      {
        mtrl_code: product.mtrl_code,
        mtrl_name: product.mtrl_name,
        cat_name: product.cat_name,
        sell_price: Number(product.sell_price),
        stock: Number(product.stock),
        satuan: product.satuan,
      },
      1,
    );
  };

  const scanMutation = useMutation({
    mutationFn: async (barcode: string) => {
      const response = await axiosInstance.get(
        `/kasir?email=${email}&mtrl_code=${barcode}`,
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success && Array.isArray(data.data) && data.data.length > 0) {
        const item: CatalogItem = data.data[0];
        handleAddToCart(item);
        toast.success("Berhasil menambahkan barang ke keranjang", {
          duration: 3000,
        });
        const audio = new Audio("/sound/beep.mp3");
        audio.play().catch((err) => console.error("Audio play error:", err));
      } else {
        toast.error("Barcode tidak ditemukan di database");
      }
    },
    onError: (error) => {
      console.error("API Error:", error);
      toast.error("Terjadi error saat memanggil API");
    },
  });

  const handleUpdate = (err: unknown, result?: Result) => {
    if (err) {
      if ((err as any).name !== "NotFoundException") {
        console.error(err);
      }
      return;
    }
    if (result) {
      const scannedText = (result as any).text ?? (result as any).getText?.();
      if (scannedText) {
        if (scannedText !== lastBarcode) {
          setLastBarcode(scannedText);
          scanMutation.mutate(scannedText);
          setTimeout(() => {
            setLastBarcode(null);
          }, 1500);
        }
      }
    }
  };

  // Jika mobile dan portrait, tampilkan pesan rotate
  if (isMobile && !isLandscape) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center w-full h-full px-6 bg-black">
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <div className="relative">
              <Smartphone className="w-20 h-20 text-white animate-bounce" />
              <div className="absolute flex items-center justify-center w-8 h-8 bg-red-500 rounded-full -right-2 -top-2 animate-pulse">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-white">
              Miringkan Perangkat Anda
            </h2>
            <p className="text-lg text-gray-300">
              Untuk menggunakan scanner barcode,
            </p>
            <p className="text-lg text-gray-300">
              harap putar perangkat ke mode landscape
            </p>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <div className="flex items-center justify-center w-12 h-20 border-2 border-gray-500 rounded-lg">
              <span className="text-xs text-gray-500">Portrait</span>
            </div>
            <svg
              className="w-8 h-8 text-red-500 animate-pulse"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            <div className="flex items-center justify-center w-20 h-12 border-2 border-green-500 rounded-lg bg-green-500/10">
              <span className="text-xs font-semibold text-green-500">
                Landscape
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tampilan scanner normal (desktop atau mobile landscape)
  return (
    <div className="fixed inset-0 w-full h-full overflow-hidden bg-black">
      <div className="absolute inset-0 w-full h-full">
        <BarcodeScanner
          width="100%"
          height="100%"
          onUpdate={handleUpdate}
          facingMode="environment"
        />
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="px-4 mb-2 text-center sm:mb-4">
          <p className="text-sm font-medium text-white sm:text-base drop-shadow-lg">
            Arahkan barcode ke dalam kotak
          </p>
        </div>

        <div
          className={`relative ${
            isMobile
              ? "w-[90vw] h-[60vh]"
              : "w-[70vw] md:w-[60vw] lg:w-[50vw] aspect-[4/3] max-w-md"
          }`}
        >
          <div className="relative w-full h-full border-2 rounded-lg sm:border-4 border-white/50">
            <div className="absolute w-6 h-6 border-t-4 border-l-4 border-red-500 rounded-tl -top-1 -left-1 sm:w-8 sm:h-8"></div>
            <div className="absolute w-6 h-6 border-t-4 border-r-4 border-red-500 rounded-tr -top-1 -right-1 sm:w-8 sm:h-8"></div>
            <div className="absolute w-6 h-6 border-b-4 border-l-4 border-red-500 rounded-bl -bottom-1 -left-1 sm:w-8 sm:h-8"></div>
            <div className="absolute w-6 h-6 border-b-4 border-r-4 border-red-500 rounded-br -bottom-1 -right-1 sm:w-8 sm:h-8"></div>

            <div className="absolute inset-x-0 top-0 h-0.5 bg-red-500 animate-scan"></div>
          </div>
        </div>

        <div className="px-4 mt-2 sm:mt-4">
          {scanMutation.isPending && (
            <div className="flex items-center gap-2 bg-blue-500/90 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm">
              <div className="w-3 h-3 border-2 border-white rounded-full sm:w-4 sm:h-4 border-t-transparent animate-spin"></div>
              <span>Memproses...</span>
            </div>
          )}
          {lastBarcode && (
            <div className="bg-green-500/90 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
              Scan: {lastBarcode}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes scan {
          0% {
            top: 0;
          }
          50% {
            top: 100%;
          }
          100% {
            top: 0;
          }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
